import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../contexts/AuthContext';

const RECENT_LIMIT = 12;

// workspace.page_recents(user_id, page_id, visited_at) 훅.
// 최근 방문 page_id 목록(visited_at desc, 상위 12) 조회 + recordVisit.
// 테이블이 아직 없을 때(마이그레이션 전)도 깨지지 않도록 try/catch 로 방어 — 빈 목록·no-op.
export default function useRecents() {
  const { user } = useAuth();
  const userId = user?.id ?? null;

  const [recentIds, setRecentIds] = useState([]); // page_id 배열 (visited_at desc)
  const [loading, setLoading] = useState(true);

  const fetchRecents = useCallback(async () => {
    if (!userId) {
      setRecentIds([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase
        .schema('workspace')
        .from('page_recents')
        .select('page_id, visited_at')
        .eq('user_id', userId)
        .order('visited_at', { ascending: false })
        .limit(RECENT_LIMIT);
      if (error) {
        setRecentIds([]);
      } else {
        setRecentIds((data || []).map((r) => r.page_id));
      }
    } catch {
      setRecentIds([]);
    }
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    fetchRecents();
  }, [fetchRecents]);

  useEffect(() => {
    const h = () => fetchRecents();
    window.addEventListener('rt:recents', h);
    return () => window.removeEventListener('rt:recents', h);
  }, [fetchRecents]);

  // 방문 기록. visited_at=now 로 upsert. 테이블 없으면 no-op.
  const recordVisit = useCallback(
    async (pageId) => {
      if (!userId || !pageId) return false;
      try {
        const { error } = await supabase
          .schema('workspace')
          .from('page_recents')
          .upsert(
            { user_id: userId, page_id: pageId, visited_at: new Date().toISOString() },
            { onConflict: 'user_id,page_id' }
          );
        if (error) return false;
      } catch {
        return false;
      }
      // 사이드바 최근 항목 갱신 신호 (현재 훅 인스턴스 + 다른 인스턴스)
      try { window.dispatchEvent(new Event('rt:recents')); } catch { /* noop */ }
      return true;
    },
    [userId]
  );

  return { recentIds, recordVisit, loading, refetch: fetchRecents };
}
